import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, Table } from 'reactstrap';

export const pct = n => n == null ? '—' : `${n > 0 ? '+' : ''}${(Number(n) * 100).toFixed(1)}%`;
export const multiple = n => n == null ? '—' : `${Number(n).toFixed(2)}x`;
const money = n => n == null ? '—' : n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : `$${(n / 1e6).toFixed(0)}M`;
const tone = n => n == null ? 'text-muted' : n > 0 ? 'text-success' : n < 0 ? 'text-danger' : '';
const SCENARIO_COLOR = { plausible: 'success', exceptional: 'warning', extreme: 'danger', implausible: 'secondary' };

function Scenario({ status }) {
  return status ? <Badge color={SCENARIO_COLOR[status] || 'light'} className="mb-scenario">{status}</Badge> : <span className="text-muted">—</span>;
}

// The five picks as the model gave them: its evidence, the screens it claims,
// and how it classified 3x, 5x and 10x from today's market cap.
export function Picks({ selections, screens, answers }) {
  return <div className="table-responsive"><Table className="align-middle">
    <thead className="table-light"><tr><th>Rank</th><th>Symbol</th><th>Picked by</th><th>Market cap</th><th>Confidence</th><th>Screens met</th><th>3x</th><th>5x</th><th>10x</th></tr></thead>
    <tbody>{selections.map(s => <React.Fragment key={s.symbol}>
      <tr>
        <td>{s.rank}</td><td className="fw-medium">{s.symbol}</td>
        <td title={(s.consensus_models || []).join(', ')}>{s.consensus_count > 1 ? <Badge color="primary">{s.consensus_count}/{answers}</Badge> : <span className="text-muted">{s.consensus_count || 1}/{answers || 1}</span>}</td>
        <td>{money(s.current_market_cap_usd)}</td><td>{s.confidence}</td>
        <td>{(s.screens_met || []).map(n => <Badge key={n} color="light" className="text-body me-1" title={screens?.[n]}>{n}</Badge>)}</td>
        <td><Scenario status={s.reverse_valuation?.scenario_3x_status} /></td>
        <td><Scenario status={s.reverse_valuation?.scenario_5x_status} /></td>
        <td><Scenario status={s.reverse_valuation?.scenario_10x_status} /></td>
      </tr>
      <tr><td colSpan={9} className="border-top-0 pt-0">
        <details><summary className="text-muted fs-13">Thesis, catalyst, reverse valuation and risks</summary><div className="fs-13 mt-2">
          <p><strong>Thesis.</strong> {s.rationale}</p>
          {s.catalyst && <p><strong>Catalyst.</strong> {s.catalyst}</p>}
          {s.fundamental_inflection && <p><strong>Fundamental inflection.</strong> {s.fundamental_inflection}</p>}
          {s.valuation && <p><strong>Valuation.</strong> {s.valuation}</p>}
          {['3x', '5x', '10x'].map(n => s.reverse_valuation?.[`scenario_${n}_requirements`] && <p key={n}><strong>{n} requires</strong> ({s.reverse_valuation[`scenario_${n}_status`] || 'unclassified'}): {s.reverse_valuation[`scenario_${n}_requirements`]}</p>)}
          {s.key_financials && <ul className="mb-2">{Object.entries(s.key_financials).map(([k, v]) => <li key={k}><strong>{k.replace(/_/g, ' ')}:</strong> {v}</li>)}</ul>}
          {!!s.risks?.length && <p className="mb-2"><strong>Risks.</strong> {s.risks.join(' · ')}</p>}
          <p className="text-muted mb-0">Price check: {s.reference_price != null ? `$${s.reference_price}` : 'not given'}{s.data_date && ` on ${s.data_date}`}{s.price_source && <> · <a href={s.price_source} target="_blank" rel="noreferrer">source</a></>}</p>
        </div></details>
      </td></tr>
    </React.Fragment>)}</tbody>
  </Table></div>;
}

// One portfolio at every checkpoint so far: each pick's return to the
// checkpoint, with the peak it reached on the way beneath it.
export function CheckpointMatrix({ selections, results, checkpoints }) {
  const reached = checkpoints.filter(c => results?.[c.id]);
  if (!reached.length) return <p className="text-muted mb-0">No checkpoint has settled yet. The first is at 1 week.</p>;
  return <div className="table-responsive"><Table className="align-middle table-nowrap mb-0">
    <thead className="table-light"><tr><th>Symbol</th>{reached.map(c => <th key={c.id}>{c.label}</th>)}</tr></thead>
    <tbody>
      {selections.map(s => <tr key={s.symbol}><td className="fw-medium">{s.symbol}</td>{reached.map(c => {
        const r = results[c.id].selections.find(x => x.symbol === s.symbol);
        return <td key={c.id}><span className={tone(r?.return)}>{pct(r?.return)}</span>
          <div className="text-muted fs-12">peak {multiple(r?.peak_multiple)}{r?.reached_3x ? ' · 3x' : r?.reached_2x ? ' · 2x' : ''}{r?.stopped_trading ? ' · stopped trading' : ''}</div></td>;
      })}</tr>)}
      <tr className="table-light"><td className="fw-medium">Portfolio</td>{reached.map(c => <td key={c.id} className={`fw-medium ${tone(results[c.id].portfolio_return)}`}>{pct(results[c.id].portfolio_return)}</td>)}</tr>
      <tr><td>SPY</td>{reached.map(c => <td key={c.id}>{pct(results[c.id].spy_return)}</td>)}</tr>
      <tr><td className="fw-medium">Excess over SPY</td>{reached.map(c => <td key={c.id} className={`fw-medium ${tone(results[c.id].excess_return)}`}>{pct(results[c.id].excess_return)}</td>)}</tr>
    </tbody>
  </Table></div>;
}

// Every model's portfolio side by side, excess over SPY at each checkpoint.
export function Comparison({ predictions, checkpoints }) {
  return <div className="table-responsive"><Table className="align-middle table-nowrap mb-0">
    <thead className="table-light"><tr><th>Model</th><th>Picks</th>{checkpoints.map(c => <th key={c.id}>{c.label}</th>)}</tr></thead>
    <tbody>{predictions.map(p => <tr key={p.id}>
      <td><Link to={`/multibagger-selection/predictions/${p.id}`}>{p.model_name}</Link></td>
      <td>{p.selections.map(s => s.symbol).join(', ')}</td>
      {checkpoints.map(c => <td key={c.id} className={tone(p.results?.[c.id]?.excess_return)}>{pct(p.results?.[c.id]?.excess_return)}</td>)}
    </tr>)}</tbody>
  </Table></div>;
}
